<template>
    <VJoyTemplate>
        <template #template-main>
            <div class="billing-information">
                <!-- TODO: move to separate component -->
                <div>
                    <VJoyPanel>
                        <template #panel-title>{{ t('projectclient.expenses.validation.summary.header') }}</template>
                        <template #panel-subtitle>{{ t('projectclient.expenses.validation.summary.purchaseorder.description') }}</template>
                        <template #panel-body>
                            <div class="view-receipt">
                                <div class="view-receipt__documentPreview / flex">
                                    <div class="view-receipt__documentPreview__title / flex" data-testid="expense-batch-summary">
                                        <div><VJoyIcon name="file-check" size="xxsmall" /></div>
                                        <div>
                                            <p>
                                                {{ t('projectclient.expenses.validation.summary.batch', [batchExpenseItem?.batchCounterInProject]) }}
                                            </p>
                                            <p>
                                                {{ batchExpenseItem?.nbExpenseInBatch }}
                                                {{
                                                    t('projectclient.expenses.validation.summary.review', {
                                                        nbExpenses: batchExpenseItem?.nbExpenseInBatch,
                                                        total: formatAmount(
                                                            projectInformation?.contract.currency,
                                                            batchExpenseItem?.summary.amountTotalWithFeesAndTaxes,
                                                            {decimals: 2},
                                                        ),
                                                        date: formatDate(batchExpenseItem?.createdDate),
                                                    })
                                                }}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="view-receipt_icons / flex">
                                        <a v-if="batchExpenseItem?.quotePdfUrl" target="_blank" :href="batchExpenseItem?.quotePdfUrl">
                                            <VJoyIcon name="download" size="xsmall" />
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <template v-if="punchoutDetails?.isPunchoutEnabled">
                                <VJoyHighlight level="info" display-icon>
                                    {{ t('projectclient.expenses.validation.punchout.highlight', [punchoutDetails?.procurementAppName]) }}
                                </VJoyHighlight>
                            </template>
                            <template v-else>
                                <VJoyInput
                                    ref="batchPurchaseOrderError"
                                    v-model="modelValue.purchaseOrderNumber"
                                    :label="t('projectclient.expenses.validation.summary.purchaseorder.title')"
                                    name="purchaseOrder"
                                    placeholder="Add a purchase order"
                                    data-testid="billing-information-purchase-order"
                                    :invalid="r$.purchaseOrderNumber?.$error"
                                />
                                <ValidationNotification
                                    v-if="!isPoNumberFormatValid"
                                    :label="t('projectclient.billinginformation.invoice.purchaseorder.regex')"
                                    type="neutral"
                                />
                                <ValidationError :errors="r$.purchaseOrderNumber?.$errors" />
                            </template>

                            <VJoyHighlight level="info" display-icon>
                                {{ t('projectclient.expenses.validation.summary.highlight') }}
                            </VJoyHighlight>
                        </template>
                    </VJoyPanel>
                    <form :id="formId" class="billing-information__form" :class="{isMobileScreen}" @submit.prevent>
                        <h3 class="section-headline">{{ t('projectclient.billinginformation.invoice') }}</h3>

                        <InjectMicroFrontendApp
                            app="compliance-mf-billing-information"
                            :props="{
                                companyId: projectInformation.client.companyId,
                                layoutVariant: 'complete',
                                showLegalInformationStatus: true,
                                showSaveButton: false,
                                page: 'timesheet_page',
                                readOnlyMode: isEnterpriseClient ? 'if-filled' : 'if-filled-and-match',
                            }"
                            class="billing-information-block"
                            @billing-information-updated-successfully="onBillingInformationUpdatedSuccessfully"
                            @billing-information-update-failed="onBillingInformationUpdateFailed"
                            @billing-information-filled-state-changed="onBillingInformationFilledStateChanged"
                        >
                            <template #loading>
                                <BillingInformationSkeleton />
                            </template>
                        </InjectMicroFrontendApp>

                        <div class="billing-information-block">
                            <template v-if="isClientCompanyAdmin && isBillingContactsWriteable">
                                <VJoyInput
                                    v-model="modelValue.billingContact"
                                    :label="t('projectclient.billinginformation.invoice.billing.contact')"
                                    name="billingContact"
                                    data-testid="billing-information-contact-email"
                                    :invalid="r$.billingContact?.$error"
                                ></VJoyInput>
                                <ValidationError :errors="r$.billingContact?.$errors" />

                                <VJoyTagsInput
                                    v-model="modelValue.otherBillingContacts"
                                    :values="modelValue.otherBillingContacts"
                                    :placeholder="t('projectclient.billinginformation.additional.billing.placeholder')"
                                    validation="email"
                                    data-testid="billing-information-other-contact-emails"
                                    :invalid-email-error="t('projectclient.general.forms.errors.allFieldsEmails')"
                                >
                                    <p class="label">
                                        {{ t('projectclient.billinginformation.invoice.additional.billing.contacts') }}
                                    </p>
                                </VJoyTagsInput>
                            </template>
                            <ReadOnlyInvoicingContacts v-else :billing-infos="modelValue" :company-id="projectInformation.client.companyId" />
                            <VJoyTagsInput
                                v-model="modelValue.otherBillingContacts"
                                :values="modelValue.otherBillingContacts"
                                :placeholder="t('projectclient.billinginformation.additional.billing.placeholder')"
                                validation="email"
                                :invalid-email-error="t('projectclient.general.forms.errors.allFieldsEmails')"
                            >
                                <p class="label">
                                    {{ t('projectclient.billinginformation.invoice.project.billing.contacts') }}
                                </p>
                            </VJoyTagsInput>
                        </div>

                        <p class="mission-confirmation">
                            {{
                                t(clientAgreementTranslationKey, {
                                    0: projectInformation.freelancer.fullName,
                                    1: formatAmount(projectInformation?.contract.currency, batchExpenseItem?.summary.amountTotalWithFeesAndTaxes, {
                                        decimals: 2,
                                    }),
                                })
                            }}
                        </p>
                    </form>
                </div>
            </div>
        </template>
    </VJoyTemplate>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useProjectInformationStore, useScreenSizeStore} from '@common/stores';
    import {BillingInformationSkeleton, ValidationError, ValidationNotification} from '@common/ui';
    import ReadOnlyInvoicingContacts from '@features/validation/ui/BillingInformation/ReadOnlyInvoicingContacts.vue';
    import type {RegleRoot} from '@regle/core';
    import {storeToRefs} from 'pinia';
    import {InjectMicroFrontendApp} from '#components';
    import {useFormat, useTranslation} from '#imports';
    import {VJoyHighlight, VJoyIcon, VJoyInput, VJoyPanel, VJoyTagsInput, VJoyTemplate} from '@maltjoy/core-vue';
    import type {ProjectValidationParam} from '@api';
    import {useExpenseValidationStore} from '../../stores';
    import type {ExpenseClientCompanyBillingInfoParam} from '../../types';

    const {formatAmount, formatDate} = useFormat();

    const {t} = useTranslation();

    const clientAgreementTranslationKey = computed(() =>
        projectInformation.value.clientBelongsToAWalletCompany
            ? 'projectclient.expenses.validation.client.agreement.for.wallet.companies'
            : 'projectclient.expenses.validation.client.agreement',
    );

    const modelValue = defineModel<ExpenseClientCompanyBillingInfoParam & ProjectValidationParam>({required: true});

    defineProps<{
        r$: RegleRoot<ExpenseClientCompanyBillingInfoParam & {purchaseOrderNumber?: string}>;
        formId: string;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: ExpenseClientCompanyBillingInfoParam & ProjectValidationParam];
        'billing-information-updated-successfully': [];
        'billing-information-update-failed': [failureReason: 'INVALID_BILLING_INFORMATION' | 'INSUFFICIENT_PERMISSIONS' | 'SERVER_ERROR'];
        'billing-information-filled-state-changed': [state: boolean];
    }>();

    const screenSizeStore = useScreenSizeStore();
    const {isMobileScreen} = storeToRefs(screenSizeStore);

    // projectInformation store
    const projectInformationStore = useProjectInformationStore();
    const {projectInformation, isClientCompanyAdmin, isEnterpriseClient} = storeToRefs(projectInformationStore);

    // expense validation store
    const expenseValidationStore = useExpenseValidationStore();
    const {batchExpenseItem} = storeToRefs(expenseValidationStore);

    const punchoutDetails = computed(() => batchExpenseItem.value?.punchoutDetails);

    const isBillingContactsWriteable = !modelValue.value.billingContact;

    // handle purchase order input
    const regex = /^(?=.*\d)[\w-]{1,25}$/;

    const isPoNumberFormatValid = computed(() => {
        if (modelValue.value.purchaseOrderNumber) {
            return regex.test(modelValue.value.purchaseOrderNumber);
        }
        return true;
    });

    const onBillingInformationUpdatedSuccessfully = () => {
        emit('billing-information-updated-successfully');
    };
    const onBillingInformationUpdateFailed = (failureReason: 'INVALID_BILLING_INFORMATION' | 'INSUFFICIENT_PERMISSIONS' | 'SERVER_ERROR') => {
        emit('billing-information-update-failed', failureReason);
    };
    const onBillingInformationFilledStateChanged = (state: boolean) => {
        emit('billing-information-filled-state-changed', state);
    };
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text';

    .billing-information {
        &__form,
        .billing-information-block {
            flex: 6;
            padding: var(--joy-core-spacing-6);
            margin-bottom: var(--joy-core-spacing-6);
            border-radius: var(--joy-core-radius-3);
            border: 1px solid var(--joy-color-neutral-30);
            background: var(--joy-color-background-default);

            &.isMobileScreen {
                margin: 0;
            }

            :deep(.joy-label),
            .label {
                font-size: var(--joy-font-size-primary-450);
                font-weight: var(--joy-font-weight-bold);
            }

            .section-headline {
                font-size: var(--joy-font-size-primary-600);
                font-weight: var(--joy-font-weight-bold);
            }

            .mission-confirmation {
                margin-top: var(--joy-core-spacing-6);
                font-size: var(--joy-font-size-primary-300);
                color: var(--joy-color-neutral-40);
            }

            .calendar-inputs {
                justify-content: space-between;
                margin-bottom: var(--joy-core-spacing-2);
                gap: var(--joy-core-spacing-4);

                &_label {
                    font-weight: var(--joy-font-weight-bold);
                    font-size: var(--joy-font-size-primary-300);
                }

                & > div {
                    flex: 1;
                }

                & > div > div {
                    margin-top: var(--joy-core-spacing-2);
                }
            }

            .find-address {
                color: var(--joy-color-secondary-30);
                margin: var(--joy-core-spacing-2) 0 var(--joy-core-spacing-6) 0;

                &:hover {
                    cursor: pointer;
                }
            }
        }

        :deep(a.joy-link.joy-link_teal) {
            text-decoration: underline;
            font-weight: var(--joy-font-weight-normal);
        }
    }

    .view-receipt {
        &__documentPreview {
            background: var(--joy-color-neutral-20);
            border-radius: var(--joy-core-radius-3);
            border: 2px solid var(--joy-color-neutral-30);
            align-items: center;
            justify-content: space-between;
            padding: var(--joy-core-spacing-6);
            flex-wrap: nowrap;

            &__title {
                align-items: center;
                flex-wrap: nowrap;

                div:first-child {
                    background: var(--joy-color-background-default);
                    border-radius: var(--joy-core-radius-2);
                    padding: var(--joy-core-spacing-2) var(--joy-core-spacing-3);
                    margin-right: var(--joy-core-spacing-6);
                }

                p:nth-child(1) {
                    @include text.getFontBySize(primary-350);

                    color: var(--joy-color-neutral-60);
                    margin-bottom: 0;
                }

                p:nth-child(2) {
                    @include text.getFontBySize(primary-300);

                    color: var(--joy-color-neutral-40);
                    margin-bottom: 0;
                }
            }
        }
    }
</style>
